import React, { useEffect, useState } from 'react';
import { Edit3, Save, X, Loader2, Heart, MessageCircle, Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { showToast } from '../../utils/errorHandler';

interface RealTalkProfile {
  is_contactable: boolean;
  real_talk_sentence: string | null;
  real_talk_tags: string[] | null;
}

interface CvPassportRealTalkProps {
  /** Read-only render (recruiter preview) — no edit affordances. */
  readOnly?: boolean;
}

const REAL_TALK_TAG_KEYS = [
  'took_career_break', 'worked_unfulfilling_jobs', 'doubted_career_path',
  'experienced_loss_passion', 'still_finding_direction', 'took_nonlinear_path',
  'rejected_more_than_accepted', 'felt_imposter_syndrome', 'chose_balance_over_pay',
  'took_paycut_for_purpose', 'prioritized_mental_health', 'experienced_layoff',
  'landed_job_by_luck', 'was_underpaid_once', 'quit_without_plan',
];

// Same 6-minute lock the CV upload pipeline uses (re-embedding collides with edits).
const checkUploadInProgress = (): number => {
  const ts = localStorage.getItem('cv_upload_timestamp');
  if (!ts) return 0;
  try {
    const elapsed = Date.now() - parseInt(ts, 10);
    const remaining = 6 * 60 * 1000 - elapsed;
    if (remaining > 0) return Math.ceil(remaining / 60000);
    localStorage.removeItem('cv_upload_timestamp');
  } catch {
    localStorage.removeItem('cv_upload_timestamp');
  }
  return 0;
};

const emptyForm = {
  is_contactable: false,
  real_talk_sentence: '',
  real_talk_tags: [] as string[],
};

const CvPassportRealTalk: React.FC<CvPassportRealTalkProps> = ({ readOnly = false }) => {
  const { t } = useTranslation(['passport', 'profile']);
  const { user, apiCall } = useAuth();

  const [profile, setProfile] = useState<RealTalkProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });

  const hydrate = (p: RealTalkProfile) => {
    setForm({
      is_contactable: p.is_contactable ?? false,
      real_talk_sentence: p.real_talk_sentence || '',
      real_talk_tags: p.real_talk_tags || [],
    });
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user?.userId) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const res = await apiCall(`${apiBase}/api/v1/profiles/${user.userId}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        setProfile(data);
        hydrate(data);
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  const startEdit = () => {
    const minutesLeft = checkUploadInProgress();
    if (minutesLeft > 0) {
      showToast(t('profile:cv.uploadWait', { minutes: minutesLeft }), 'info');
      return;
    }
    if (profile) hydrate(profile);
    setEditing(true);
  };

  const cancelEdit = () => {
    if (profile) hydrate(profile);
    setEditing(false);
  };

  const save = async () => {
    const minutesLeft = checkUploadInProgress();
    if (minutesLeft > 0) {
      showToast(t('profile:cv.uploadWait', { minutes: minutesLeft }), 'info');
      return;
    }
    try {
      setSaving(true);
      const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const payload = {
        is_contactable: form.is_contactable,
        real_talk_sentence: form.real_talk_sentence,
        real_talk_tags: form.real_talk_tags,
      };
      const res = await apiCall(`${apiBase}/api/v1/profiles/${user?.userId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(60000),
      });
      if (res.ok) {
        const updated = await res.json();
        setProfile(updated);
        hydrate(updated);
        setEditing(false);
        showToast(t('passport:realTalk.savedToast'), 'success');
      } else {
        showToast(t('profile:contactInfo.error', 'Failed to update. Please try again.'), 'error');
      }
    } catch {
      showToast(t('profile:contactInfo.error', 'Failed to update. Please try again.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> …
      </div>
    );
  }

  const canEdit = !readOnly && !editing;

  return (
    <div className="max-w-2xl mx-auto px-3 sm:px-4 py-4 space-y-4">
      {/* Explorer-only banner — Real Talk is never shown to recruiters. */}
      <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-pink-50 border border-pink-200">
        <Eye className="w-3.5 h-3.5 text-pink-500 flex-shrink-0 mt-0.5" />
        <p className="text-[11px] sm:text-xs text-pink-800 leading-relaxed">{t('passport:explorerOnly.hint')}</p>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-sm sm:text-base font-semibold text-slate-800">{t('passport:realTalk.editTitle')}</h3>
          <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5">{t('passport:realTalk.editSubtitle')}</p>
        </div>
        {canEdit && (
          <button
            onClick={startEdit}
            className="flex items-center gap-1 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5 text-pink-600 hover:text-pink-700 hover:bg-pink-100 rounded-lg transition-colors flex-shrink-0"
          >
            <Edit3 className="w-3.5 h-3.5" />
            {t('passport:common.edit')}
          </button>
        )}
      </div>

      {!editing ? (
        /* ─── View mode ─── */
        <div className="space-y-4">
          {/* Real Talk text */}
          <div>
            <label className="text-[11px] sm:text-xs text-slate-500 flex items-center gap-1.5 mb-2">
              <Heart className="w-3.5 h-3.5 text-pink-600" /> {t('profile:realTalk.title')}
            </label>
            {form.real_talk_sentence || form.real_talk_tags.length > 0 ? (
              <div className="space-y-2">
                {form.real_talk_sentence && (
                  <p className="text-xs sm:text-sm text-slate-700 italic">"{form.real_talk_sentence}"</p>
                )}
                {form.real_talk_tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {form.real_talk_tags.map(tag => (
                      <span key={tag} className="px-2 py-0.5 bg-pink-100 text-pink-700 text-[10px] sm:text-xs font-medium rounded-full border border-pink-200">
                        {t(`profile:realTalk.tags.${tag}`)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs sm:text-sm text-slate-500">{t('profile:realTalk.notSet')}</p>
            )}
          </div>

          {/* Contactable */}
          <div className="pt-3 border-t border-slate-100">
            <label className="text-[11px] sm:text-xs text-slate-500 flex items-center gap-1.5">
              <MessageCircle className="w-3.5 h-3.5 text-pink-600" /> {t('profile:contactInfo.allowContact')}
            </label>
            <p className="text-xs sm:text-sm text-slate-700 mt-0.5">
              {profile?.is_contactable ? t('profile:contactInfo.yes') : `❌ ${t('profile:contactInfo.no')}`}
            </p>
          </div>
        </div>
      ) : (
        /* ─── Edit mode ─── */
        <div className="space-y-4">
          {/* Real Talk */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Heart className="w-4 h-4 text-pink-600" />
              <h4 className="text-sm font-semibold text-slate-800">{t('profile:realTalk.title')}</h4>
            </div>
            <textarea
              value={form.real_talk_sentence}
              onChange={(e) => { if (e.target.value.length <= 500) setForm({ ...form, real_talk_sentence: e.target.value }); }}
              placeholder={t('profile:realTalk.sentencePlaceholder')}
              maxLength={500}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 resize-none"
            />
            <div className="flex flex-wrap gap-2 mt-2">
              {REAL_TALK_TAG_KEYS.map(tag => {
                const isSelected = form.real_talk_tags.includes(tag);
                const canSelect = form.real_talk_tags.length < 20 || isSelected;
                return (
                  <button
                    key={tag}
                    onClick={() => {
                      if (isSelected) setForm({ ...form, real_talk_tags: form.real_talk_tags.filter(x => x !== tag) });
                      else if (canSelect) setForm({ ...form, real_talk_tags: [...form.real_talk_tags, tag] });
                    }}
                    disabled={!canSelect && !isSelected}
                    className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                      isSelected
                        ? 'bg-pink-500 text-white shadow-sm'
                        : canSelect
                        ? 'bg-white text-slate-700 border border-slate-300 hover:border-pink-400 hover:bg-pink-50'
                        : 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
                    }`}
                  >
                    {t(`profile:realTalk.tags.${tag}`)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Contactable */}
          <div className="bg-pink-50/50 p-3 rounded-lg border border-pink-200">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_contactable}
                onChange={(e) => setForm({ ...form, is_contactable: e.target.checked })}
                className="w-4 h-4 mt-0.5 text-pink-600 border-slate-300 rounded focus:ring-pink-500"
              />
              <span>
                <span className="text-sm font-medium text-slate-800">{t('profile:contactInfo.allowContact')}</span>
                <span className="block text-xs text-slate-600 mt-0.5">{t('profile:contactInfo.allowContactHint')}</span>
              </span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-3 border-t border-slate-100">
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg hover:from-pink-600 hover:to-rose-600 disabled:opacity-50 transition-all shadow-sm"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? t('passport:common.saving') : t('passport:common.save')}
            </button>
            <button
              onClick={cancelEdit}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm border border-slate-300 text-slate-600 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              <X className="w-4 h-4" />
              {t('passport:common.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CvPassportRealTalk;
